import { useState, useCallback, useRef, useEffect } from 'react';

// Updated streaming hook with JWT token support
export const useStreamingAnalysis = (advertiserToken, apiUrl = 'https://madkpi-bot-135392845747.us-central1.run.app/analyze') => {
  const [blocks, setBlocks] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const tokenRef = useRef(advertiserToken);

  // Update token ref when token changes
  useEffect(() => {
    tokenRef.current = advertiserToken;
  }, [advertiserToken]);

  const analyzeData = useCallback(async (question) => {
    if (!question.trim() || isStreaming) return;

    // Check if token is available
    if (!tokenRef.current) {
      setError('Advertiser token not available. Please wait...');
      return;
    }

    setIsStreaming(true);
    setError(null);
    setBlocks([]);

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenRef.current}`,
        },
        body: JSON.stringify({ question }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim()) {
            try {
              const block = JSON.parse(line);
              setBlocks(prev => [...prev, block]);
            } catch (parseError) {
              console.error('Failed to parse block:', line, parseError);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      
      console.error('Streaming error:', err);
      setError(err.message);
      
      setBlocks(prev => [...prev, {
        type: 'error',
        timestamp: new Date().toISOString(),
        data: {
          message: 'Connection failed',
          details: err.message
        }
      }]);
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [apiUrl, isStreaming]);

  const cancelAnalysis = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  }, []);

  const setBlocksDirectly = useCallback((newBlocks) => {
    setBlocks(newBlocks || []);
    setError(null);
  }, []);

  return {
    blocks,
    isStreaming,
    error,
    analyzeData,
    cancelAnalysis,
    setBlocksDirectly,
  };
};